const PDF_URL = "https://new.facetbarcelona.com/JTS/php/pdf_generator.php";

/**
 * Genera PDF individual de asignación a engastador externo
 * @param {string} userName - Nombre del usuario asignado
 * @param {string} mo - Número de MO
 * @param {string} fechaAsignacion - Fecha de asignación (YYYY-MM-DD)
 * @param {string} generatedByName - Nombre del usuario que genera el PDF
 * @param {Object} stoneMeasures - Objeto con las medidas de las piedras: { "MO": { "piedraCode": "medida" } }
 * @param {Object} stonePrices - Objeto con los precios de las piedras: { "MO": { "piedraCode": "precio" } }
 * @param {Object} repairMeasuresByMo - Medidas manuales para reparaciones por MO: { "MO": [ { numeroPiedras, medida1, medida2, precioManual? } ] }; precioManual opcional por línea, si > 0 se usa en lugar de tarifa estándar
 * @param {Object} preciosManuales - Precios manuales por MO y código: { "MO": { "codigoPiedra": number } }; solo entradas con valor > 0
 * @returns {Promise<{pdfBlob: Blob, filename: string}|false>} PDF como blob y nombre del archivo o false si hay error
 */
export const generateSingleAssignmentPDF = async (
  userName,
  mo,
  fechaAsignacion,
  generatedByName,
  stoneMeasures = {},
  stonePrices = {},
  repairMeasuresByMo = {},
  preciosManuales = {},
  entregaOverrides = null
) => {
  try {
    const medidas = stoneMeasures[mo] || {};
    const precios = stonePrices[mo] || {};
    
    const bodyData = {
      screen: 'pdf_generator',
      action: 'generate_single_assignment',
      userName: userName,
      mo: mo,
      fechaAsignacion: fechaAsignacion,
      generatedByName: generatedByName
    };

    if (repairMeasuresByMo && Object.keys(repairMeasuresByMo).length > 0) {
      bodyData.reparacion_medidas = repairMeasuresByMo;
    }

    if (preciosManuales && Object.keys(preciosManuales).length > 0) {
      bodyData.precios_manuales = preciosManuales;
    }
    
    if (Object.keys(medidas).length > 0) {
      bodyData.Medidas = medidas;
    }
    
    if (Object.keys(precios).length > 0) {
      bodyData.Precios = precios;
    }

    // Solo mandar overrides cuando haya cambios
    if (
      entregaOverrides &&
      typeof entregaOverrides === "object" &&
      Object.keys(entregaOverrides).length > 0
    ) {
      bodyData.entrega_overrides = entregaOverrides;
    }
    
    const response = await fetch(PDF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyData)
    });

    if (!response.ok) {
      throw new Error('Error al generar PDF');
    }

    let filename = null;
    
    for (const [key, value] of response.headers.entries()) {
      if (key.toLowerCase() === 'x-filename') {
        filename = value;
        break;
      }
    }
    
    if (!filename) {
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) {
          filename = match[1].trim().replace(/^["']|["']$/g, '');
        }
      }
    }

    const pdfBlob = await response.blob();
    const finalFilename = filename || `Asignacion_Engastador_${mo}_${new Date().toISOString().split('T')[0]}.pdf`;

    return { pdfBlob, filename: finalFilename };
  } catch (error) {
    console.error("Error al generar PDF individual:", error);
    return false;
  }
};

/**
 * Genera PDF consolidado de asignaciones múltiples a engastador externo
 * @param {string} userName - Nombre del usuario asignado
 * @param {Array<string>} moList - Lista de números de MO
 * @param {string} fechaAsignacion - Fecha de asignación (YYYY-MM-DD)
 * @param {string} generatedByName - Nombre del usuario que genera el PDF
 * @param {Object} stoneMeasures - Objeto con las medidas de las piedras por MO: { "MO": { "piedraCode": "medida" } }
 * @param {Object} stonePrices - Objeto con los precios de las piedras por MO: { "MO": { "piedraCode": "precio" } }
 * @param {Object} repairMeasuresByMo - Medidas manuales para reparaciones por MO: { "MO": [ { numeroPiedras, medida1, medida2, precioManual? } ] }; precioManual opcional por línea, si > 0 se usa en lugar de tarifa estándar
 * @param {Object} preciosManuales - Precios manuales por MO y código: { "MO": { "codigoPiedra": number } }; solo entradas con valor > 0
 * @returns {Promise<{pdfBlob: Blob, filename: string}|false>} PDF como blob y nombre del archivo o false si hay error
 */
export const generateBulkAssignmentPDF = async (
  userName,
  moList,
  fechaAsignacion,
  generatedByName,
  stoneMeasures = {},
  stonePrices = {},
  repairMeasuresByMo = {},
  preciosManuales = {},
  entregaOverrides = null
) => {
  try {
    const medidas = {};
    const precios = {};
    moList.forEach(mo => {
      if (stoneMeasures[mo] && Object.keys(stoneMeasures[mo]).length > 0) {
        Object.assign(medidas, stoneMeasures[mo]);
      }
      if (stonePrices[mo] && Object.keys(stonePrices[mo]).length > 0) {
        Object.assign(precios, stonePrices[mo]);
      }
    });
    
    const bodyData = {
      screen: 'pdf_generator',
      action: 'generate_bulk_assignment',
      userName: userName,
      moList: JSON.stringify(moList),
      fechaAsignacion: fechaAsignacion,
      generatedByName: generatedByName
    };

    if (repairMeasuresByMo && Object.keys(repairMeasuresByMo).length > 0) {
      bodyData.reparacion_medidas = repairMeasuresByMo;
    }

    if (preciosManuales && Object.keys(preciosManuales).length > 0) {
      bodyData.precios_manuales = preciosManuales;
    }
    
    if (Object.keys(medidas).length > 0) {
      bodyData.Medidas = medidas;
    }
    
    if (Object.keys(precios).length > 0) {
      bodyData.Precios = precios;
    }

    // Solo mandar overrides cuando haya cambios
    if (
      entregaOverrides &&
      typeof entregaOverrides === "object" &&
      Object.keys(entregaOverrides).length > 0
    ) {
      bodyData.entrega_overrides = entregaOverrides;
    }
    
    const response = await fetch(PDF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyData)
    });

    if (!response.ok) {
      throw new Error('Error al generar PDF');
    }

    let filename = null;
    
    for (const [key, value] of response.headers.entries()) {
      if (key.toLowerCase() === 'x-filename') {
        filename = value;
        break;
      }
    }
    
    if (!filename) {
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) {
          filename = match[1].trim().replace(/^["']|["']$/g, '');
        }
      }
    }

    const pdfBlob = await response.blob();
    const finalFilename = filename || `WO000001.pdf`;
    
    return { pdfBlob, filename: finalFilename };
  } catch (error) {
    console.error("Error al generar PDF consolidado:", error);
    return false;
  }
};


